import { type ColumnDef, DashboardIcon, EmptyState, Inline, ListView, type ListViewDef, PageContent, type SortState } from '@olytic/foundation';
import { type ReactNode, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { accessOf } from './auth/api';
import { useCurrentUser } from './auth/AuthProvider';
import { useDocumentTitle } from './documentTitle';
import { appsWithTab, mayOpenTab, TAB_KINDS, type TabKind, TABS } from './tabs';

interface TabRow {
  key: string;
  label: string;
  icon: ReactNode;
  href: string;
  kind: TabKind;
  kindLabel: string;
  apps: string;
  team: boolean;
}

const ROWS: TabRow[] = TABS.map((tab) => ({
  key: tab.key,
  label: tab.label,
  icon: tab.icon,
  href: tab.href,
  kind: tab.kind,
  kindLabel: TAB_KINDS.find((k) => k.kind === tab.kind)?.singular ?? tab.kind,
  apps: appsWithTab(tab.key).map((a) => a.label).join(', '),
  team: mayOpenTab(tab, 'team'),
}));

const COLUMNS: ColumnDef<TabRow>[] = [
  {
    key: 'label',
    label: 'Tab',
    primary: true,
    sortable: true,
    width: 280,
    get: (row) => row.label,
    href: (row) => row.href,
    render: (row) => (
      <Inline as="span" gap="sm" wrap={false}>
        {row.icon}
        {row.label}
      </Inline>
    ),
  },
  { key: 'kindLabel', label: 'Kind', sortable: true, get: (row) => row.kindLabel },
  { key: 'apps', label: 'In apps', sortable: true, get: (row) => row.apps || 'None' },
];

/** One view per kind of tab, after All. A kind with no tabs yet (reports) has no view. */
const VIEWS: ListViewDef<TabRow>[] = [
  { id: 'all', label: 'All tabs' },
  ...TAB_KINDS.filter(({ kind }) => ROWS.some((r) => r.kind === kind)).map(({ kind, label }) => ({
    id: kind,
    label,
    filter: (row: TabRow) => row.kind === kind,
  })),
];

/** Every tab the person may open, whichever apps include it, so a tab outside the current app is one click away. */
export function AllTabsPage(): JSX.Element {
  useDocumentTitle('All tabs');
  const access = accessOf(useCurrentUser());
  const navigate = useNavigate();
  const [viewId, setViewId] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState | undefined>(undefined);

  const rows = useMemo(() => {
    const view = VIEWS.find((v) => v.id === viewId);
    const q = search.trim().toLowerCase();
    return ROWS.filter((row) => access === 'all' || row.team).filter((row) => !view?.filter || view.filter(row)).filter(
      (row) => !q || `${row.label} ${row.kindLabel} ${row.apps}`.toLowerCase().includes(q),
    );
  }, [access, viewId, search]);

  return (
    <PageContent as="section" width="fill">
      <ListView
        objectLabel="All tabs"
        objectIcon={<DashboardIcon />}
        columns={COLUMNS}
        rows={rows}
        getRowId={(row) => row.key}
        mode="client"
        views={VIEWS}
        activeViewId={viewId}
        onViewChange={setViewId}
        search={search}
        onSearchChange={setSearch}
        {...(sort ? { sort } : {})}
        onSortChange={setSort}
        selectable={false}
        pagination="none"
        onRowClick={(row) => navigate(row.href)}
        empty={
          <EmptyState
            icon={<DashboardIcon />}
            title={search.trim() ? `No tabs match “${search.trim()}”` : 'No tabs'}
            description={search.trim() ? 'Try a different search.' : undefined}
          />
        }
      />
    </PageContent>
  );
}
